'use client';

import { clsx } from 'clsx';

import styles from './DallasLink.module.scss';
import Link, { LinkProps } from 'next/link';
import { HTMLAttributeAnchorTarget, PropsWithChildren } from 'react';
import {
  DALLAS_TEXT_COLOR_CLASSNAME_SEGMENTS,
  DallasTextColor,
} from '../types/typography';

export interface Props
  extends Pick<LinkProps, 'as' | 'prefetch' | 'replace' | 'onClick'> {
  id?: string;
  href?: LinkProps['href'];
  target?: HTMLAttributeAnchorTarget;
  className?: string;
  color?: DallasTextColor | 'inherit';
  download?: string | null;
  underlined?: boolean;
}

export default function DallasLink({
  id,
  as,
  href,
  replace,
  prefetch = false,
  children,
  underlined,
  className: providedClassName,
  color = 'info',
  download = null,
  onClick,
  ...linkProps
}: PropsWithChildren<Props>) {
  const isExternal = typeof href === 'string' && !href.startsWith('/');
  const className = clsx(
    providedClassName,
    styles.root,
    color != 'inherit' &&
      styles[`color-${DALLAS_TEXT_COLOR_CLASSNAME_SEGMENTS[color]}`],
    underlined && styles.underlined,
  );

  return isExternal ? (
    <a
      id={id}
      href={href}
      className={className}
      onClick={onClick}
      {...linkProps}
    >
      {children}
    </a>
  ) : href != null ? (
    download && typeof href === 'string' ? (
      <a
        download={download}
        id={id}
        href={href}
        className={className}
        onClick={onClick}
        {...linkProps}
      >
        {children}
      </a>
    ) : (
      <Link
        id={id}
        href={href}
        className={className}
        as={as}
        prefetch={prefetch}
        replace={replace}
        onClick={onClick}
        {...linkProps}
      >
        {children}
      </Link>
    )
  ) : (
    <span id={id} className={className} onClick={onClick}>
      {children}
    </span>
  );
}
